import React, { useState } from 'react'
import { Button, Confirm } from 'semantic-ui-react'

import { useCanEditDeck } from 'redux/deck/selectors'

import { useAppSelector } from 'redux/hooks'
import { Snapshot } from 'services/apiTypes/deck.types'

import DeckService from 'services/deck.service'
import SaveService from 'services/save.service'
import ToastService from 'services/toast.service'

import Link from 'components/elements/Link'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import SimpleSpinner from 'components/elements/SimpleSpinner'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import Icon from 'components/elements/Icon'

import { DECK_DELINEATOR, SNAPSHOT_DELINEATOR } from 'pages/compare/index'

import styles from './deckVersionsRow.module.scss'

type Props = {
  snapshot: Snapshot
}

const DeckVersionsRow = ({ snapshot }: Props) => {
  const deckId = useAppSelector(state => state.deck.id)
  const canEditDeck = useCanEditDeck()

  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [removed, setRemoved] = useState(false)
  const [confirmRestore, setConfirmRestore] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)

  const handleRemoveSnapshot = () => {
    setLoading(true)

    return DeckService.deleteSnapshot(snapshot.id)
      .then(() => setRemoved(true))
      .catch(() => ToastService.create('Unable to remove deck snapshot', 'Deck snapshots', 'error'))
      .finally(() => setLoading(false))
  }

  if (removed) return null

  const handleRestore = () => {
    setLoading(true)
    setConfirmRestore(false)

    DeckService.getSnapshot(snapshot.id)
      .then(res => SaveService.overwriteDeck(res))
      .catch(() => ToastService.create('Unable to restore deck snapshot', 'Deck snapshots', 'error'))
      .finally(() => setLoading(false))
  }

  return (
    <>
      <tr>
        <td>
          <button className={styles.expandTrigger} onClick={() => setExpanded(!expanded)}>
            <Icon name="info circle" />
          </button>
        </td>
        <td>
          <Link className={styles.link} href={`/snapshots/${snapshot.id}`}>
            {snapshot.name}
          </Link>
        </td>
        <td>{new Date(snapshot.createdAt).toLocaleString()}</td>
        <td className={styles.optionsColumn}>
          {loading ? (
            <SimpleSpinner size="xSmall" />
          ) : (
            <ArchidektDropdown
              menuClassName={styles.dropdownOptions}
              options={[
                {
                  label: 'View deck comparison',
                  icon: 'exchange',
                  to: `/compare/?one=${DECK_DELINEATOR}${deckId}&two=${SNAPSHOT_DELINEATOR}${snapshot.id}`,
                },
                {
                  label: 'Remove deck snapshot',
                  icon: 'trash',
                  hidden: !canEditDeck,
                  onClick: () => setConfirmRemove(true),
                },
                {
                  label: 'Restore deck snapshot',
                  icon: 'undo alternate',
                  onClick: () => setConfirmRestore(true),
                },
              ]}
            />
          )}
        </td>
      </tr>
      <tr className={`${expanded && styles.selected} ${styles.details}`}>
        {expanded && <td colSpan={4}>{snapshot.description}</td>}
      </tr>

      <Confirm
        closeIcon
        open={confirmRestore}
        onCancel={() => setConfirmRestore(false)}
        onConfirm={handleRestore}
        cancelButton="Cancel"
        confirmButton={<Button color="blue">Restore Deck Snapshot</Button>}
        content={
          <div className={styles.restoreWarning}>
            <p>This will restore your deck to the exact state of this version.</p>

            <p>
              Doing so will remove any cards not included in the saved version, replace any matching cards with the
              exact state they were saved as, and re-add removed cards as new cards.
            </p>

            <p>Are you sure you want to continue?</p>
          </div>
        }
      />

      <ConfirmDeleteModal
        usedInOverlay
        header="Remove deck snapshot"
        buttonText="Delete snapshot"
        loadingMessage="Removing deck snapshot..."
        message={`This action cannot be undone. Are you sure you want to remove the snapshot ${snapshot.name}?`}
        delayEnabledTime={2000}
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onDelete={handleRemoveSnapshot}
      />
    </>
  )
}
export default DeckVersionsRow
